import { api } from "../api";
import { hasPortalFeature } from "../utils/portal-features";
import type { SchuelerSteckbrief } from "../types";
import { esc } from "../utils/html";

const DEFAULT_MAX_LENGTH = 2000;

function showToast(message: string): void {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = message;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add("show"), 10);
  setTimeout(() => { t.classList.remove("show"); setTimeout(() => t.remove(), 300); }, 2500);
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export async function render(el: HTMLElement) {
  // Ohne die Freigabe-Funktion bleibt der Steckbrief rein schulintern
  const canRelease = hasPortalFeature("steckbriefFreigabe");

  el.innerHTML = `
    <div style="max-width:900px;margin:0 auto;padding:1rem">
      <h1 style="font-size:1.5rem;font-weight:600;margin-bottom:.25rem">Mein Steckbrief</h1>
      <p style="color:var(--gray-600);font-size:.9rem;margin-bottom:1.25rem">
        Stelle dich kurz vor: deine Interessen, bisherigen Erfahrungen, was du im Praktikum lernen möchtest.
        ${canRelease ? "Wenn du den Steckbrief freigibst, kann die Praktikumsstelle ihn vor deinem Einsatz lesen." : ""}
      </p>
      <div id="sb-content"><div class="spinner"></div></div>
    </div>
  `;

  const content = el.querySelector<HTMLElement>("#sb-content")!;

  let data: SchuelerSteckbrief;
  try {
    data = await api.schuelerApp.steckbrief();
  } catch (err: any) {
    content.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }

  const maxLength = data.maxLength ?? DEFAULT_MAX_LENGTH;
  const initialText = data.text ?? "";

  content.innerHTML = `
    <div class="card" style="padding:1.25rem">
      <div class="form-group">
        <label for="sb-text" style="font-weight:500">Steckbrief</label>
        <textarea id="sb-text" maxlength="${maxLength}" rows="12"
                  style="width:100%;padding:.75rem;border:1px solid var(--gray-300);border-radius:.375rem;font-family:inherit;font-size:.95rem;line-height:1.4;resize:vertical"
                  placeholder="Erzähle etwas über dich …">${esc(initialText)}</textarea>
        <div style="display:flex;justify-content:flex-end;font-size:.8rem;color:var(--gray-600);margin-top:.25rem">
          <span id="sb-counter">${initialText.length} / ${maxLength} Zeichen</span>
        </div>
      </div>

      ${canRelease ? `
      <div class="form-group" style="margin-top:1rem">
        <label style="display:flex;align-items:flex-start;gap:.5rem;cursor:pointer">
          <input id="sb-released" type="checkbox" ${data.released ? "checked" : ""} style="margin-top:.2rem"/>
          <span>
            <strong>Für Praktikumsstellen freigeben</strong><br/>
            <span style="font-size:.85rem;color:var(--gray-600)">
              Wenn aktiv, sehen Praktikumsstellen deinen Steckbrief in ihrer Praktikumsplanung.
            </span>
          </span>
        </label>
      </div>` : ""}

      <div style="display:flex;align-items:center;gap:1rem;margin-top:1.25rem">
        <button id="sb-save" class="btn btn-primary">Speichern</button>
        <span id="sb-updated" style="font-size:.85rem;color:var(--gray-600)">
          ${data.updatedAt ? `Zuletzt aktualisiert: ${esc(formatDate(data.updatedAt))}` : ""}
        </span>
      </div>
    </div>
  `;

  const textarea = content.querySelector<HTMLTextAreaElement>("#sb-text")!;
  const counter = content.querySelector<HTMLElement>("#sb-counter")!;
  const released = content.querySelector<HTMLInputElement>("#sb-released");
  const saveBtn = content.querySelector<HTMLButtonElement>("#sb-save")!;
  const updatedEl = content.querySelector<HTMLElement>("#sb-updated")!;

  textarea.addEventListener("input", () => {
    counter.textContent = `${textarea.value.length} / ${maxLength} Zeichen`;
  });

  saveBtn.addEventListener("click", async () => {
    saveBtn.disabled = true;
    try {
      const result = await api.schuelerApp.steckbriefSave(textarea.value, !!released?.checked);
      updatedEl.textContent = result.updatedAt ? `Zuletzt aktualisiert: ${formatDate(result.updatedAt)}` : "";
      showToast("Gespeichert");
    } catch (err: any) {
      showToast("Fehler: " + (err.message || "Speichern fehlgeschlagen"));
    } finally {
      saveBtn.disabled = false;
    }
  });
}
